import { isEmpty, isNull } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { LessonState, Point } from '../../helpers/constants';
import { toCSS } from '../../helpers/styles';
import { IPoint } from '../../helpers/types';
import { calculateTextPosition, getDashArray } from '../../helpers/utils';
import useResource from '../../hooks/useResource';
import useResourceSlice from '../../hooks/useResourceSlice';
import {
  updateAnimatedTrackPosition_AC,
  updatePendingAnimations_AC,
} from '../../store/actions/executor.actions';
import {
  getIntersectionPoint,
  getPointAtDistance,
  isPointInsideRectangle,
  nauticalMileToMeter,
} from '../../utils/math.util';
import { ANIMATED_ELEMENT_TYPES, AnimatedElement } from './AnimatedPoint';
import HookablePoint from './HookablePoint';
import useMapLocation from './hooks/useMapLocation';
import MapSizeContext from './MapSizeContext';

export interface INewTrackProps {
  point: any;
  resource: string;
  monitor: string;
  trackNumber: string;
  moveThroughTrajectory: string;
  showTrajectory: boolean;
  fontColor: string;
  hookProperties: any;
  circles: Array<any>;
  rectangles: Array<any>;
  hookSize?: number;
  trackSymbolHeight?: number;
  trackSymbolWidth?: number;
  labelFontSize?: number;
  centralLeftLabel?: string;
  centralRightLabel?: string;
  bottomLeftLabel?: string;
  bottomRightLabel?: string;
  topLeftLabel?: string;
}

interface Position {
  x: number;
  y: number;
  width: number;
  height: number;
  duration: number;
}

const StyledImage = styled.image<any>`
  cursor: pointer;
  ${({ x, y, width, height, duration }) => `
        x: ${toCSS(+x - +width / 2)};
        y: ${toCSS(+y - +height / 2)};
        transition: x ${duration}ms ease, y ${duration}ms ease;
    `};
`;

const AnimatedStyledCircle = styled.circle<any>``;

const StyledCircle = styled.circle<any>`
  ${({ duration }) => `
  transition: cx ${duration}ms ease, cy ${duration}ms ease;
`};
`;

const StyledPath = styled.path<any>`
  ${({ duration }) => `
    transition: d ${duration}ms ease;
`};
`;

const AnimatedStyledText = styled.text<any>``;

const StyledText = styled.text<any>`
  ${({ transitionTime, posX, posY }) => `
    transform: translate(${toCSS(+posX)},${toCSS(+posY)});
    transition: transform ${transitionTime}ms ease;
`};
`;

const getSideOfRectangle = (
  points,
  position
): {
  start: IPoint;
  end: IPoint;
} => {
  const { p1, p2, p3, p4 } = points;
  switch (position) {
    case 'sideOne':
      return {
        start: p2,
        end: p1,
      };
    case 'sideTwo':
      return {
        start: p2,
        end: p3,
      };
    case 'sideThree':
      return {
        start: p3,
        end: p4,
      };
    case 'sideFour':
      return {
        start: p1,
        end: p4,
      };
    default:
      return {
        start: p1,
        end: p2,
      };
  }
};

const getRectagleSidePath = ({
  start,
  end,
}: {
  start: IPoint;
  end: IPoint;
}) => {
  return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
};

interface IRectanglePoints {
  p1: IPoint;
  p2: IPoint;
  p3: IPoint;
  p4: IPoint;
}

/**
 * We need to know if the label of the rectangle is being placed on the right or left
 * on the top or bottom, we need to manipulate the textAnchor based on that
 */
const labelPositionAdjustment = (
  allPoints: IRectanglePoints,
  rectangleNamePosition: string
) => {
  // edge selection
  const selectedSide = getSideOfRectangle(allPoints, rectangleNamePosition);

  // calculate mid point of edge
  const mid = {
    x: (selectedSide.start.x + selectedSide.end.x) / 2,
    y: (selectedSide.start.y + selectedSide.end.y) / 2,
  };

  /**
   * Calculate the center of the new rectangle that we are drawing.
   */
  const center = getIntersectionPoint(
    allPoints.p1.x,
    allPoints.p1.y,
    allPoints.p3.x,
    allPoints.p3.y,
    allPoints.p4.x,
    allPoints.p4.y,
    allPoints.p2.x,
    allPoints.p2.y
  );

  /**
   * get a point 20px along the line joining the mid of the selected side and the center of the rectangle
   */
  const textPointPosition = getPointAtDistance(
    mid.x,
    mid.y,
    center.x,
    center.y,
    -20
  );
  return {
    textAnchor: 'start',
    posX: textPointPosition.x,
    posY: textPointPosition.y,
  };
};

export default function NewTrack(props: INewTrackProps) {
  const {
    point,
    resource,
    monitor,
    trackNumber,
    moveThroughTrajectory,
    showTrajectory,
    fontColor,
    hookProperties,
    circles = [],
    rectangles = [],
    hookSize,
    trackSymbolHeight,
    trackSymbolWidth,
    labelFontSize,
    centralLeftLabel,
    centralRightLabel,
    bottomLeftLabel,
    bottomRightLabel,
    topLeftLabel,
  } = props;

  const trajectory = useResourceSlice(moveThroughTrajectory);
  const [transitionTime, setTransitionTime] = useState(0);
  const [transitionIndex, setTransitionIndex] = useState<any>(null);
  const [transitionCoord, setTransitionCoord] = useState<any>([]);
  const [transitionCoordColor, setTransitionCoordColor] = useState('yellow');
  const lessonStatus = useSelector((state: any) => state.executor.status);
  const [rectangleRotatin, setRectangleRotation] = useState<any>(null);
  const labels: Array<{ position: string; value: any }> = [
    { position: 'topLeft', value: topLeftLabel },
    { position: 'topRight', value: trackNumber },
    { position: 'centralLeft', value: centralLeftLabel },
    { position: 'centralRight', value: centralRightLabel },
    { position: 'bottomLeft', value: bottomLeftLabel },
    { position: 'bottomRight', value: bottomRightLabel },
  ];
  const [isMediaShowing, setIsMediaShowing] = useState<Boolean>(false);

  const multimediaFromStore = useSelector(
    (state: any) => state.executor.multimediaText
  );

  useEffect(() => {
    setIsMediaShowing(!isEmpty(multimediaFromStore));
  }, [multimediaFromStore]);

  const [originalPointPosition, setOriginalPointPosition] = useState<any>({
    x: 0,
    y: 0,
  });

  const [recRectangles, setRecRectangles] = useState<any>(rectangles);

  const dispatch = useDispatch();

  const intervalRef = useRef<any>();
  const result = useResource(resource);
  const [url, setUrl] = useState(result);
  const sizes = useContext(MapSizeContext);
  const { latLangToXY, rerender, distanceToPixel } = useMapLocation(
    monitor,
    sizes
  );
  const [position, setPosition] = useState({
    x: 0,
    y: 0,
    latitude: '',
    longitude: '',
  });

  useEffect(() => {
    // if the store asks for animation start, then start the animation.
    if (trajectory) {
      const { defaultTransitionTime } = trajectory;
      setTransitionIndex(0);
      setTransitionTime(defaultTransitionTime);
      setTransitionCoord([]);
      dispatch(updatePendingAnimations_AC(1, true));
    }
  }, [trajectory]);

  useEffect(() => {
    // if rectangles are defined in the store then we need to set recalculated rectangles
    // thse recalculatedRectangles have their center calculated in pixels.
    if (rectangles.length > 0) {
      setRecRectangles(
        rectangles.map((rectangle) => {
          const { center } = rectangle;
          const latitude = `${center.latV} ${center.latH}`;
          const longitude = `${center.longV} ${center.longH}`;
          const [x, y] = latLangToXY(longitude, latitude);
          return {
            ...rectangle,
            pixelCenter: {
              x,
              y,
            },
          };
        })
      );
    }
  }, [rectangles]);

  useEffect(() => {
    // if the position is updated (here our main concern is due to range in range out)
    // we need to recalculate the rectangle center position and also the dx and dy, the
    // pixel distance between the track and rectangle center.
    if (position.latitude) {
      if (recRectangles.length > 0) {
        setRecRectangles((rects) =>
          rects.map((rectangle) => {
            const { center } = rectangle;
            const latitude = `${center.latV} ${center.latH}`;
            const longitude = `${center.longV} ${center.longH}`;
            const [x, y] = latLangToXY(longitude, latitude);
            return {
              ...rectangle,
              dx: position.x - originalPointPosition.x,
              dy: position.y - originalPointPosition.y,
              pixelCenter: {
                x,
                y,
              },
            };
          })
        );
      }
    }
  }, [position]);

  useEffect(() => {
    // if the lesson is not paused, the do animate the track by updating the transitionIndex on timeout
    if (lessonStatus === LessonState.STARTED && !isMediaShowing) {
      if (transitionIndex !== null) {
        const { coordinates } = trajectory;
        if (coordinates[transitionIndex]) {
          const point = coordinates[transitionIndex];
          const latitude = `${point.latV} ${point.latH}`;
          const longitude = `${point.longV} ${point.longH}`;
          const [translatedX, translatedY] = latLangToXY(longitude, latitude);
          let dx = translatedX - originalPointPosition.x;
          let dy = translatedY - originalPointPosition.y;

          let rotation =
            Math.atan((translatedX - position.x) / (translatedY - position.y)) +
            Math.PI / 2;

          setPosition({
            x: translatedX,
            y: translatedY,
            latitude,
            longitude,
          });

          setRecRectangles((recalculatedRectangles: any) =>
            recalculatedRectangles.map((recalculatedRectangle) => ({
              ...recalculatedRectangle,
              rotation: isNaN(rotation)
                ? recalculatedRectangle.rotation
                : rotation,
              dx,
              dy,
            }))
          );

          setTransitionCoord((existingCoord: any) => [
            ...existingCoord,
            {
              x: position.x,
              y: position.y,
              originalPoint: point,
              pointColor: point.pointColor || transitionCoordColor,
            },
          ]);
          point.pointColor && setTransitionCoordColor(point.pointColor);
          point.transitionTime && setTransitionTime(point.transitionTime);
          intervalRef.current = setTimeout(() => {
            setTransitionIndex((prevIndex: any) => {
              if (prevIndex !== null) {
                return prevIndex + 1;
              }
              return prevIndex;
            });
          }, point.transitionTime || transitionTime);
        } else {
          dispatch(updatePendingAnimations_AC(-1, true));
        }
      }
    }
    return () => {
      intervalRef.current && clearTimeout(intervalRef.current);
    };
  }, [transitionIndex, lessonStatus, isMediaShowing]);

  useEffect(() => {
    // if we are showing the trajectory coordinates, they have to be recaluclated in
    // accordance with the range in and range out.
    setTransitionCoord((coordinates: any) =>
      coordinates.map((pointObj: any) => {
        const { originalPoint } = pointObj;
        const point = originalPoint;
        const latitude = `${point.latV} ${point.latH}`;
        const longitude = `${point.longV} ${point.longH}`;
        const [translatedX, translatedY] = latLangToXY(longitude, latitude);
        return {
          ...pointObj,
          x: translatedX,
          y: translatedY,
        };
      })
    );
  }, [rerender]);

  useEffect(() => {
    // when range in or range out is pressed we have to recalculate the position of track
    // we also keep track of the original Position, because that tells us how far away or close
    // we have to keep the rectangles.
    const latitude = position.latitude
      ? position.latitude
      : `${point.latV} ${point.latH}`;
    const longitude = position.longitude
      ? position.longitude
      : `${point.longV} ${point.longH}`;
    const [translatedX, translatedY] = latLangToXY(longitude, latitude);

    setPosition({
      x: translatedX,
      y: translatedY,
      latitude,
      longitude,
    });
    if (originalPointPosition.latitude && originalPointPosition.longitude) {
      // the contents of this if seem to be redundant. Can be removed after checking.
      const [transX, transY] = latLangToXY(
        originalPointPosition.longitude,
        originalPointPosition.latitude
      );
      setOriginalPointPosition({
        ...originalPointPosition,
        x: transX,
        y: transY,
      });
    } else {
      setOriginalPointPosition({
        x: translatedX,
        y: translatedY,
        latitude,
        longitude,
      });
    }
    setUrl(result);
  }, [point, result, rerender]);

  useEffect(() => {
    // update the track animated position to store, this ensures the track connectors are drawn properly
    dispatch(updateAnimatedTrackPosition_AC(position, trackNumber));
  }, [position]);

  const pauseAnimation = !(
    lessonStatus === LessonState.STARTED && !isMediaShowing
  );

  const getRectanglePath2 = (
    center: any = {},
    rotation: number,
    length: number,
    width: number,
    dx: number,
    dy: number,
    pointPosition: number
  ) => {
    const l = distanceToPixel(nauticalMileToMeter(length));
    const w = distanceToPixel(nauticalMileToMeter(width));
    const theta = Math.PI - rotation;
    const tx = center.x;
    const ty = center.y;
    const adjCx = tx + (dx || 0);
    const adjCy = ty + (dy || 0);
    const newCoord = {
      p1: {
        x: -l / 2,
        y: -w / 2,
      },
      p2: {
        x: -l / 2,
        y: w / 2,
      },
      p3: {
        x: l / 2,
        y: w / 2,
      },
      p4: {
        x: l / 2,
        y: -w / 2,
      },
      pointOnAxis: {
        x: -l / 2 + (l * pointPosition) / 100,
        y: 0,
      },
    };

    const reverseRotation = (x: number, y: number, angle: number) => ({
      x: x * Math.cos(angle) - y * Math.sin(angle),
      y: x * Math.sin(angle) + y * Math.cos(angle),
    });

    const rotated = {
      p1: {
        ...reverseRotation(newCoord.p1.x, newCoord.p1.y, theta),
      },
      p2: {
        ...reverseRotation(newCoord.p2.x, newCoord.p2.y, theta),
      },
      p3: {
        ...reverseRotation(newCoord.p3.x, newCoord.p3.y, theta),
      },
      p4: {
        ...reverseRotation(newCoord.p4.x, newCoord.p4.y, theta),
      },
      pointOnAxis: {
        ...reverseRotation(
          newCoord.pointOnAxis.x,
          newCoord.pointOnAxis.y,
          theta
        ),
      },
    };

    const translate = (
      x: number,
      y: number,
      originX: number,
      originY: number
    ) => ({
      x: x + originX,
      y: y + originY,
    });

    const translated = {
      p1: {
        ...translate(rotated.p1.x, rotated.p1.y, adjCx, adjCy),
      },
      p2: {
        ...translate(rotated.p2.x, rotated.p2.y, adjCx, adjCy),
      },
      p3: {
        ...translate(rotated.p3.x, rotated.p3.y, adjCx, adjCy),
      },
      p4: {
        ...translate(rotated.p4.x, rotated.p4.y, adjCx, adjCy),
      },
      pointOnAxis: {
        ...translate(
          rotated.pointOnAxis.x,
          rotated.pointOnAxis.y,
          adjCx,
          adjCy
        ),
      },
    };
    return {
      ...translated,
    };
  };

  const generateLinePath = (points: {
    p1: Point;
    p2: Point;
    p3: Point;
    p4: Point;
  }) => {
    const { p1, p2, p3, p4 } = points;
    return `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p3.x} ${p3.y} L ${p4.x} ${p4.y} Z`;
  };

  return (
    <>
      {url && (
        <HookablePoint
          x={position.x}
          transitionTime={transitionTime}
          y={position.y}
          id={trackNumber}
          imageUrl={url}
          latitude={position.latitude}
          longitude={position.longitude}
          hookProperties={hookProperties}
          hookSize={hookSize}
          trackSymbolHeight={trackSymbolHeight}
          trackSymbolWidth={trackSymbolWidth}
          pauseAnimation={pauseAnimation}
        />
      )}
      {labels.map((label) => {
        const { posX, posY, textAnchor } = calculateTextPosition(
          {
            x: position.x,
            y: position.y,
          },
          label.position,
          labelFontSize || 8,
          15
        );
        return (
          <AnimatedElement
            key={label.position}
            x={0}
            y={0}
            pauseAnimation={pauseAnimation}
            duration={transitionTime}
            elementType={ANIMATED_ELEMENT_TYPES.TEXT}
            transitionObject={{
              transform: `translate(${toCSS(posX.toFixed(2))}, ${toCSS(
                posY.toFixed(2)
              )})`,
            }}
            textAnchor={textAnchor}
            pointName={label.value}
            fontWeight="normal"
            fontSize={labelFontSize || '0.5em'}
            strokeWidth="0.5px"
            fill={fontColor ?? 'yellow'}
          />

          // <StyledText
          //   key={label.position}
          //   x="0"
          //   y="0"
          //   transitionTime={transitionTime}
          //   fill={fontColor}
          //   strokeWidth="0.5px"
          //   fontSize={labelFontSize || '0.5em'}
          //   fontWeight="normal"
          //   {...calculateTextPosition(
          //     {
          //       x: position.x,
          //       y: position.y,
          //     },
          //     label.position,
          //     labelFontSize || 8,
          //     15
          //   )}
          // >
          //   {label.value?.toUpperCase()}
          // </StyledText>
        );
      })}
      {circles.map((circle: any, index: number) => (
        <AnimatedElement
          pauseAnimation={pauseAnimation}
          elementType={ANIMATED_ELEMENT_TYPES.CIRCLE}
          duration={transitionTime}
          transitionObject={{
            cx: position.x,
            cy: position.y,
          }}
          key={index}
          r={distanceToPixel(nauticalMileToMeter(circle.radius))}
          fill="none"
          stroke="yellow"
          strokeDasharray={getDashArray(circle.lineType)}
        />

        // <AnimatePath
        //   pauseAnimation={pauseAnimation}
        //   duration={transitionTime ?? 500}
        //   transitionPoints={{ cx: position.x, cy: position.y }}
        //   id={`circle_${trackNumber}_${index}`}
        //   key={index}
        // >
        //   <AnimatedStyledCircle
        //     // cx={position.x}
        //     // cy={position.y}
        //     r={distanceToPixel(nauticalMileToMeter(circle.radius))}
        //     fill="none"
        //     stroke="yellow"
        //     // key={index}
        //     id={`circle_${trackNumber}_${index}`}
        //     duration={transitionTime}
        //   />
        // </AnimatePath>
      ))}
      {recRectangles.map((rectangle: any, index: number) => {
        const { pointOnAxis, ...allPoints } = getRectanglePath2(
          rectangle.pixelCenter || rectangle.center,
          rectangle.rotation,
          rectangle.length,
          rectangle.width,
          rectangle.dx,
          rectangle.dy,
          rectangle.pointPosition
        );

        const calculatedTextPos = calculateTextPosition(
          {
            x: pointOnAxis.x,
            y: pointOnAxis.y,
          },
          rectangle.pointNamePosition,
          rectangle.labelFontSize || 10
        );
        const rectangleTextPos = labelPositionAdjustment(
          allPoints as IRectanglePoints,
          rectangle.rectangleNamePosition
        );
        return (
          <>
            <AnimatedElement
              pauseAnimation={pauseAnimation}
              elementType={ANIMATED_ELEMENT_TYPES.PATH}
              // duration={!isNull(transitionIndex) && transitionTime}
              transitionObject={{
                d: generateLinePath(allPoints),
              }}
              key={index}
              fill="none"
              stroke={rectangle.lineColor || 'yellow'}
              strokeWidth="2"
              pointerEvents="none"
            />

            {/* <StyledPath
              d={generateLinePath(allPoints)}
              stroke={rectangle.lineColor || 'yellow'}
              strokeWidth="2"
              fill="none"
              pointerEvents="none"
              key={index}
              // duration={!isNull(transitionIndex) && transitionTime}
            /> */}

            <AnimatedElement
              pauseAnimation={pauseAnimation}
              elementType={ANIMATED_ELEMENT_TYPES.CIRCLE}
              // duration={!isNull(transitionIndex) && transitionTime}
              transitionObject={{
                cx: pointOnAxis.x,
                cy: pointOnAxis.y,
              }}
              r={2}
              fill={rectangle.lineColor || 'yellow'}
              stroke="none"
            />

            {/* <StyledCircle
              cx={pointOnAxis.x}
              cy={pointOnAxis.y}
              r="2"
              fill={rectangle.lineColor || 'yellow'}
              duration={!isNull(transitionIndex) && transitionTime}
              stroke="none"
            /> */}

            <AnimatedElement
              fill={rectangle.lineColor ?? 'yellow'}
              x={0}
              y={0}
              pauseAnimation={pauseAnimation}
              // duration={!isNull(transitionIndex) && transitionTime}
              elementType={ANIMATED_ELEMENT_TYPES.TEXT}
              transitionObject={{
                transform: `translate(${toCSS(
                  calculatedTextPos.posX.toFixed(2)
                )}, ${toCSS(calculatedTextPos.posY.toFixed(2))})`,
              }}
              textAnchor={calculatedTextPos.textAnchor}
              pointName={rectangle.pointName}
              fontSize={rectangle.labelFontSize}
            />

            {/* <StyledText
              stroke={rectangle.lineColor || 'yellow'}
              font-size={rectangle.labelFontSize}
              x="0"
              y="0"
              {...calculatedTextPos}
              transitionTime={!isNull(transitionIndex) && transitionTime}
            >
              {rectangle.pointName?.toUpperCase()}
            </StyledText> */}

            {/**
             * The styled path here is another approach that we are not using right now
             * the idea is to draw the text of the rectangle name along the path of a selected side.
             */}
            <AnimatedElement
              pauseAnimation={pauseAnimation}
              elementType={ANIMATED_ELEMENT_TYPES.PATH}
              // duration={!isNull(transitionIndex) && transitionTime}
              transitionObject={{
                d: getRectagleSidePath(
                  getSideOfRectangle(allPoints, rectangle.rectangleNamePosition)
                ),
              }}
              id="myPath"
            />

            {/* <StyledPath
              d={getRectagleSidePath(
                getSideOfRectangle(allPoints, rectangle.rectangleNamePosition)
              )}
              id="myPath"
            /> */}

            <AnimatedElement
              fill={rectangle.lineColor ?? 'yellow'}
              x={0}
              y={0}
              pauseAnimation={pauseAnimation}
              // duration={!isNull(transitionIndex) && transitionTime}
              elementType={ANIMATED_ELEMENT_TYPES.TEXT}
              transitionObject={{
                transform: `translate(${toCSS(
                  rectangleTextPos.posX.toFixed(2)
                )}, ${toCSS(rectangleTextPos.posY.toFixed(2))})`,
              }}
              textAnchor={rectangleTextPos.textAnchor}
              pointName={rectangle.rectangleName}
              fontSize={rectangle.labelFontSize}
            />

            {/* <StyledText
              x={0}
              y={0}
              {...labelPositionAdjustment(
                allPoints as IRectanglePoints,
                rectangle.rectangleNamePosition
              )}
              stroke={rectangle.lineColor || 'yellow'}
              font-size={rectangle.labelFontSize}
              transitionTime={!isNull(transitionIndex) && transitionTime}
            >
              <textPath href="#myPath">
              {rectangle.rectangleName?.toUpperCase()}
              </textPath>
            </StyledText> */}
          </>
        );
      })}
      {showTrajectory &&
        transitionCoord.map((transitionPoint: any, index: number) => (
          <circle
            cx={transitionPoint.x}
            cy={transitionPoint.y}
            dur={transitionTime}
            fill={transitionPoint.pointColor}
            stroke={
              position.x === transitionPoint.x &&
              position.y === transitionPoint.y
                ? 'none'
                : transitionPoint.pointColor
            }
            r="2.5"
            key={index}
          />
        ))}
    </>
  );
}
